import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { format, addDays, subDays } from "date-fns";
import {
  Loader2, ChevronLeft, ChevronRight, MapPin, Navigation, Clock,
  AlertTriangle, ExternalLink, Route as RouteIcon, LocateFixed,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
  loadGoogleMaps, openExternal, isIOS, googleNavUrl, appleNavUrl,
  formatDistance, formatDuration, pinIcon,
} from "../../utils/googleMaps";
import { toast } from "sonner";
import useTechLocationSharing from "../../hooks/useTechLocationSharing";
import LocationDisclosureDialog, {
  LOCATION_DISCLOSURE_KEY,
  hasAcceptedLocationDisclosure,
} from "../../components/tech/LocationDisclosureDialog";

const API = process.env.REACT_APP_BACKEND_URL;
const authCfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("tech_token")}` } });

const STATUS_FALLBACK = "#3B82F6";

// Technician day map: numbered stops in appointment order, road route between
// them with per-leg distance/time + daily totals. Never reorders jobs.
const TechJobMap = ({ onOpenJob }) => {
  const [date, setDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapError, setMapError] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const overlays = useRef([]);
  const { enabled: sharing, loaded: sharingLoaded, setSharing } = useTechLocationSharing();
  const [togglingShare, setTogglingShare] = useState(false);
  const [showDisclosure, setShowDisclosure] = useState(false);

  const fetchDay = useCallback(async (d) => {
    setLoading(true);
    setSelectedGroup(null);
    try {
      const res = await axios.get(`${API}/api/tech/map/day?date=${d}`, authCfg());
      setData(res.data);
    } catch (e) {
      setData({ stops: [], unmapped: [], route: null, error: e?.response?.data?.detail || "Failed to load jobs" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDay(date); }, [date, fetchDay]);

  // Build / rebuild the map whenever data changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!data || !mapRef.current) return;
      let maps;
      try {
        maps = await loadGoogleMaps("tech_token");
      } catch (e) {
        setMapError("Couldn't load Google Maps. Ask your admin to enable the Maps JavaScript API for the company's Google key.");
        return;
      }
      if (cancelled) return;
      if (!mapObj.current) {
        mapObj.current = new maps.Map(mapRef.current, {
          center: { lat: 39.5, lng: -98.35 },
          zoom: 4,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          gestureHandling: "greedy",
          clickableIcons: false,
          styles: [
            { featureType: "poi", stylers: [{ visibility: "off" }] },
            { featureType: "transit", stylers: [{ visibility: "off" }] },
          ],
        });
      }
      const map = mapObj.current;
      overlays.current.forEach((o) => o.setMap(null));
      overlays.current = [];

      const stops = data.stops || [];
      if (!stops.length) return;

      // Group stops that share coordinates so stacked appointments stay reachable
      const groups = new Map();
      stops.forEach((s) => {
        const key = `${s.lat.toFixed(5)},${s.lng.toFixed(5)}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(s);
      });

      const bounds = new maps.LatLngBounds();
      groups.forEach((groupStops) => {
        const first = groupStops[0];
        const label = groupStops.length > 1
          ? `${first.stop_number}+`
          : String(first.stop_number);
        const marker = new maps.Marker({
          position: { lat: first.lat, lng: first.lng },
          map,
          label: { text: label, color: "#ffffff", fontSize: "13px", fontWeight: "700" },
          icon: pinIcon(maps, first.status_color || STATUS_FALLBACK, { size: 44, withDot: false }),
          title: groupStops.map((s) => s.title).join(", "),
        });
        marker.addListener("click", () => setSelectedGroup(groupStops));
        overlays.current.push(marker);
        bounds.extend({ lat: first.lat, lng: first.lng });
      });

      // Road route (never straight lines) — one polyline per leg
      (data.route?.legs || []).forEach((leg) => {
        if (!leg.polyline) return;
        const path = maps.geometry.encoding.decodePath(leg.polyline);
        const line = new maps.Polyline({
          path, map,
          strokeColor: "#2563eb", strokeOpacity: 0.85, strokeWeight: 4,
        });
        overlays.current.push(line);
      });

      if (stops.length === 1) {
        map.setCenter({ lat: stops[0].lat, lng: stops[0].lng });
        map.setZoom(14);
      } else {
        map.fitBounds(bounds, 48);
      }
    })();
    return () => { cancelled = true; };
  }, [data]);

  const navigate = (stop, app) => {
    const dest = stop.customer_address || `${stop.lat},${stop.lng}`;
    openExternal(app === "apple" ? appleNavUrl(dest) : googleNavUrl(dest));
  };

  const toggleSharing = async (on) => {
    // Play/App Store policy: show the background-location disclosure BEFORE
    // any system permission prompt is triggered.
    if (on && !hasAcceptedLocationDisclosure()) {
      setShowDisclosure(true);
      return;
    }
    setTogglingShare(true);
    try {
      await setSharing(on);
    } catch (_) {
      toast.error("Couldn't update location sharing — check your connection and try again");
    }
    setTogglingShare(false);
  };

  const acceptDisclosure = async () => {
    localStorage.setItem(LOCATION_DISCLOSURE_KEY, "1");
    setShowDisclosure(false);
    await toggleSharing(true);
  };

  const route = data?.route;
  const stops = data?.stops || [];
  const unmapped = data?.unmapped || [];

  return (
    <div className="space-y-3" data-testid="tech-job-map">
      <LocationDisclosureDialog
        open={showDisclosure}
        onAccept={acceptDisclosure}
        onCancel={() => setShowDisclosure(false)}
      />
      {/* Date selector + sharing control */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => setDate(format(subDays(new Date(`${date}T12:00:00`), 1), "yyyy-MM-dd"))} data-testid="map-date-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <input
            type="date"
            value={date}
            onChange={(e) => e.target.value && setDate(e.target.value)}
            className="h-8 px-2 rounded-lg border border-border bg-card text-foreground text-sm"
            data-testid="map-date-input"
          />
          <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => setDate(format(addDays(new Date(`${date}T12:00:00`), 1), "yyyy-MM-dd"))} data-testid="map-date-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setDate(format(new Date(), "yyyy-MM-dd"))} data-testid="map-date-today">
            Today
          </Button>
        </div>
        {sharingLoaded && (
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-xl bg-card border border-border/60" data-testid="location-sharing-control">
            <LocateFixed className={`w-3.5 h-3.5 ${sharing ? "text-green-500" : "text-muted-foreground"}`} />
            <span className="text-xs text-muted-foreground">Share location</span>
            <Switch checked={sharing} disabled={togglingShare} onCheckedChange={toggleSharing} data-testid="location-sharing-switch" />
          </div>
        )}
      </div>

      {/* Totals bar */}
      <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap" data-testid="map-day-totals">
        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {stops.length} stop{stops.length !== 1 ? "s" : ""}</span>
        {route && (
          <>
            <span className="flex items-center gap-1"><RouteIcon className="w-3.5 h-3.5" /> {formatDistance(route.total_distance_m)} driving</span>
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {formatDuration(route.total_duration_s)} total drive</span>
          </>
        )}
        {!route && stops.length >= 2 && !loading && (
          <span className="flex items-center gap-1 text-amber-500"><AlertTriangle className="w-3.5 h-3.5" /> Road routing unavailable right now</span>
        )}
        {unmapped.length > 0 && (
          <span className="flex items-center gap-1 text-amber-500" data-testid="map-unmapped-count">
            <AlertTriangle className="w-3.5 h-3.5" /> {unmapped.length} not on map
          </span>
        )}
      </div>

      {/* Map */}
      <div className="relative rounded-2xl overflow-hidden border border-border/60" style={{ height: "52vh", minHeight: 320 }}>
        <div ref={mapRef} className="absolute inset-0" data-testid="map-canvas" />
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/60 z-10">
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
          </div>
        )}
        {mapError && (
          <div className="absolute inset-0 flex items-center justify-center bg-background z-10 p-6 text-center">
            <p className="text-sm text-muted-foreground max-w-sm" data-testid="map-error">{mapError}</p>
          </div>
        )}
        {!loading && !mapError && stops.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/70 z-10 p-6 text-center">
            <p className="text-sm text-muted-foreground" data-testid="map-empty">
              {data?.error || "No mappable jobs for this day"}
            </p>
          </div>
        )}

        {/* Selected stop(s) card */}
        {selectedGroup && (
          <div className="absolute left-2 right-2 bottom-2 z-20 rounded-xl bg-card border border-border shadow-xl max-h-[46%] overflow-y-auto" data-testid="map-stop-card">
            <div className="flex items-center justify-between px-3 pt-2">
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                {selectedGroup.length > 1 ? `${selectedGroup.length} appointments at this address` : `Stop ${selectedGroup[0].stop_number}`}
              </span>
              <button onClick={() => setSelectedGroup(null)} className="text-muted-foreground text-sm px-1" data-testid="map-stop-card-close">✕</button>
            </div>
            {selectedGroup.map((s) => (
              <div key={s.job_id} className="px-3 py-2 border-b border-border/40 last:border-0" data-testid={`map-stop-${s.job_id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="w-5 h-5 rounded-full text-[10px] font-bold text-white flex items-center justify-center" style={{ background: s.status_color || STATUS_FALLBACK }}>{s.stop_number}</span>
                  <span className="text-sm font-semibold text-foreground">{s.customer_name}</span>
                  <Badge className="text-[10px] px-1.5 border" style={{ background: `${s.status_color || STATUS_FALLBACK}12`, color: s.status_color || STATUS_FALLBACK, borderColor: `${s.status_color || STATUS_FALLBACK}25` }}>{s.status_label}</Badge>
                  {s.assignment_pending && <Badge className="text-[10px] px-1.5 bg-orange-500/10 text-orange-500 border border-orange-500/20">Not accepted yet</Badge>}
                  {s.no_time && <Badge className="text-[10px] px-1.5 bg-amber-500/10 text-amber-500 border border-amber-500/20">No time set</Badge>}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{s.customer_address}</p>
                <p className="text-xs text-muted-foreground">
                  {s.scheduled_time ? `${s.scheduled_time}${s.scheduled_time_end ? ` – ${s.scheduled_time_end}` : ""}` : "No scheduled time"}
                </p>
                <div className="flex gap-2 mt-1.5">
                  {s.assignment_pending ? (
                    <span className="text-[11px] text-orange-500 self-center" data-testid={`map-accept-hint-${s.job_id}`}>
                      Accept this job from the List view to open it
                    </span>
                  ) : (
                    <Button size="sm" className="h-7 text-xs" onClick={() => onOpenJob?.(s.job_id)} data-testid={`map-open-job-${s.job_id}`}>
                      Open Job
                    </Button>
                  )}
                  {isIOS() && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate(s, "apple")} data-testid={`map-nav-apple-${s.job_id}`}>
                      <Navigation className="w-3 h-3 mr-1" /> Apple Maps
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate(s, "google")} data-testid={`map-nav-google-${s.job_id}`}>
                    <Navigation className="w-3 h-3 mr-1" /> Google Maps
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Stop list with per-leg travel info */}
      {stops.length > 0 && (
        <div className="rounded-2xl border border-border/60 bg-card divide-y divide-border/40" data-testid="map-stop-list">
          {stops.map((s, i) => (
            <React.Fragment key={s.job_id}>
              {i > 0 && route?.legs?.[i - 1] && (
                <div className="px-4 py-1 flex items-center gap-2 text-[11px] text-muted-foreground bg-muted/30" data-testid={`map-leg-${i}`}>
                  <RouteIcon className="w-3 h-3" />
                  {formatDistance(route.legs[i - 1].distance_m)} · {formatDuration(route.legs[i - 1].duration_s)} drive
                </div>
              )}
              <button className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-muted/40" onClick={() => setSelectedGroup([s])} data-testid={`map-list-stop-${s.job_id}`}>
                <span className="w-6 h-6 rounded-full text-[11px] font-bold text-white flex items-center justify-center flex-shrink-0" style={{ background: s.status_color || STATUS_FALLBACK }}>{s.stop_number}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground truncate">{s.customer_name} — {s.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{s.scheduled_time || "No time"} · {s.customer_address}</p>
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              </button>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Unmappable jobs */}
      {unmapped.length > 0 && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-3" data-testid="map-unmapped-list">
          <p className="text-xs font-semibold text-amber-500 mb-1.5 flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5" /> Jobs that can't be shown on the map
          </p>
          {unmapped.map((u) => (
            <div key={u.job_id} className="flex items-center justify-between gap-2 py-1">
              <div className="min-w-0">
                <p className="text-sm text-foreground truncate">{u.customer_name} — {u.title}</p>
                <p className="text-[11px] text-muted-foreground">
                  {u.reason === "no_address" ? "No address on the job" : "Address couldn't be located"}
                  {u.no_time ? " · no scheduled time" : ""}
                </p>
              </div>
              <Button size="sm" variant="outline" className="h-7 text-xs flex-shrink-0" onClick={() => onOpenJob?.(u.job_id)} data-testid={`map-unmapped-open-${u.job_id}`}>
                Open
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TechJobMap;
